import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API from '@/lib/api';
import { FlyerDialog } from '@/components/flyer/FlyerDialog';
import { LeadUpdates } from '@/components/LeadUpdates';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import {
  ArrowLeft, Save, FileSignature, Mail, Download, Send, Home,
  Users, Building2, Eye, DollarSign, TrendingUp, Wrench,
  Clock, CheckCircle2, XCircle, Phone, MessageSquare, Activity,
  ChevronRight, Zap, Trash2, Upload, Paperclip, X,
  MapPin, ExternalLink, Scale, CalendarIcon, FileImage } from 'lucide-react';
import { toast } from 'sonner';
import { Document, Page, pdfjs } from 'react-pdf';

import AddressAutocomplete from '@/components/AddressAutocomplete';
import DripAssignmentCard from '@/components/DripAssignmentCard';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import DealBlastSection from '@/components/DealBlastSection';
import BlastHistorySection from '@/components/BlastHistorySection';
import { BestBuyersCard } from '@/components/buyers/BestBuyersCard';
import { HotMatchBanner } from '@/components/buyers/HotMatchBanner';
import { TasksPanel } from '@/components/tasks/TasksPanel';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const STAGES = ['New', 'Offer Sent', 'Follow-Up', 'Price Watch', 'Rejected Countered', 'Rejected NC', 'Accepted Other Offer', 'Under Contract', 'Marketed', 'Sold', 'Closed', 'Dead', 'Cancelled'];

const stageConfig = {
  'New': { color: '#0073ea', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', badgeBg: 'bg-blue-100' },
  'Offer Sent': { color: '#a25ddc', bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', badgeBg: 'bg-purple-100' },
  'Follow-Up': { color: '#fdab3d', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', badgeBg: 'bg-amber-100' },
  'Rejected Countered': { color: '#F97316', bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', badgeBg: 'bg-orange-100' },
  'Rejected NC': { color: '#F43F5E', bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200', badgeBg: 'bg-rose-100' },
  'Accepted Other Offer': { color: '#64748B', bg: 'bg-slate-50', text: 'text-slate-700', border: 'border-slate-200', badgeBg: 'bg-slate-100' },
  'Price Watch': { color: '#EC4899', bg: 'bg-pink-50', text: 'text-pink-700', border: 'border-pink-200', badgeBg: 'bg-pink-100' },
  'Under Contract': { color: '#784bd1', bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200', badgeBg: 'bg-indigo-100' },
  'Marketed': { color: '#00a0ff', bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200', badgeBg: 'bg-cyan-100' },
  'Sold': { color: '#00c875', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', badgeBg: 'bg-emerald-100' },
  'Closed': { color: '#037f4c', bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', badgeBg: 'bg-green-100' },
  'Dead': { color: '#e2445c', bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', badgeBg: 'bg-red-100' },
  'Cancelled': { color: '#9ca3af', bg: 'bg-gray-50', text: 'text-gray-600', border: 'border-gray-300', badgeBg: 'bg-gray-200' },
};

const activityIcons = {
  status_change: Activity,
  contract_generated: FileSignature,
  email_sent: Mail,
  buyer_blast: Users,
  note: MessageSquare,
  call: Phone,
  follow_up: Clock,
};

const inputClass = "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-[#0073ea]/50 focus:ring-[#0073ea]";

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-gray-500 text-xs uppercase tracking-wider">{label}</Label>
      {children}
    </div>
  );
}

function SectionHeader({ title, icon: Icon }) {
  return (
    <div className="flex items-center gap-2 mb-4">
      {Icon && <Icon className="h-4 w-4 text-[#0073ea]" strokeWidth={1.5} />}
      <h3 className="text-base font-semibold text-gray-900">{title}</h3>
    </div>
  );
}

/* ─── Status Dropdown ─── */
function StatusStepper({ currentStatus, onStatusChange }) {
  const cfg = stageConfig[currentStatus] || stageConfig['New'];

  return (
    <div data-testid="status-stepper" className="flex items-center gap-3">
      <span className="text-sm font-medium text-gray-500">Stage:</span>
      <Select value={currentStatus} onValueChange={onStatusChange}>
        <SelectTrigger
          data-testid="stage-dropdown"
          className={`flex-1 sm:flex-none sm:w-52 h-9 text-sm font-semibold ${cfg.badgeBg} ${cfg.text} ${cfg.border} border`}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-white border-gray-200">
          {STAGES.map(stage => {
            const sc = stageConfig[stage];
            return (
              <SelectItem key={stage} value={stage} className="text-gray-900">
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: sc.color }} />
                  {stage}
                </div>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
}

/* ─── Deal Summary Box ─── */
function DealSummary({ lead, onUpdate }) {
  const ucp = lead.under_contract_price || 0;
  const mp = lead.marketing_price || 0;
  const sp = lead.sold_price || 0;
  const calcFee = sp > 0 && ucp > 0 ? sp - ucp : (mp > 0 && ucp > 0 ? mp - ucp : 0);

  const fmt = (v) => v ? `$${Number(v).toLocaleString()}` : '--';

  return (
    <Card data-testid="deal-summary" className="bg-white border-gray-200 shadow-sm">
      <CardContent className="p-5">
        <SectionHeader title="Deal Summary" icon={DollarSign} />
        {/* Row 1: Core pricing */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-3">
          <div className="p-3 rounded-lg bg-[#f6f7fb] border border-gray-100">
            <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">Asking Price</p>
            <p className="text-sm font-semibold text-gray-900 font-mono">{fmt(lead.asking_price)}</p>
          </div>
          <div className="p-3 rounded-lg bg-[#f6f7fb] border border-gray-100">
            <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">Offer Amount</p>
            <p className="text-sm font-semibold text-gray-900 font-mono">{fmt(lead.offer_amount)}</p>
          </div>
          <div className="p-3 rounded-lg bg-[#f6f7fb] border border-gray-100">
            <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">ARV</p>
            <p className="text-sm font-semibold text-gray-900 font-mono">{fmt(lead.arv)}</p>
          </div>
          <div className="p-3 rounded-lg bg-[#f6f7fb] border border-gray-100">
            <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">Repair Est.</p>
            <p className="text-sm font-semibold text-gray-900 font-mono">{fmt(lead.repair_estimate)}</p>
          </div>
          <div className="p-3 rounded-lg bg-[#f6f7fb] border border-gray-100">
            <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">Escrow</p>
            <p className="text-sm font-semibold text-gray-900 font-mono">{lead.escrow_deposit || '--'}</p>
          </div>
        </div>

        {(lead.status === 'Rejected Countered' || lead.counter_amount > 0) && (
          <div className="mb-3 flex flex-wrap items-center gap-3 rounded-lg border border-orange-200 bg-orange-50 p-3" data-testid="counter-box">
            <div className="flex-1 min-w-[160px]">
              <p className="text-[10px] uppercase tracking-wider text-orange-600 mb-1.5">Seller Counter Amount</p>
              <Input data-testid="counter-amount" type="number" value={lead.counter_amount || ''} onChange={e => onUpdate('counter_amount', parseFloat(e.target.value) || 0)}
                placeholder="$0" className="h-8 bg-white border-orange-200 text-sm font-mono" />
            </div>
            {lead.counter_amount > 0 && lead.offer_amount > 0 && (
              <div className="text-right" data-testid="counter-gap">
                <p className="text-[10px] uppercase tracking-wider text-orange-600">Gap vs your offer</p>
                <p className="text-sm font-bold font-mono text-orange-700">{fmt(lead.counter_amount - lead.offer_amount)} ({Math.round(((lead.counter_amount - lead.offer_amount) / lead.offer_amount) * 100)}%)</p>
              </div>
            )}
          </div>
        )}

        {/* Row 2: Deal pricing - editable */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="p-3 rounded-lg bg-indigo-50 border border-indigo-100">
            <p className="text-[10px] uppercase tracking-wider text-indigo-400 mb-1.5">Under Contract Price</p>
            <Input
              data-testid="under-contract-price"
              type="number"
              value={ucp || ''}
              onChange={e => onUpdate('under_contract_price', parseFloat(e.target.value) || 0)}
              placeholder="$0"
              className="h-8 text-sm font-mono font-semibold bg-white border-indigo-200 text-indigo-900"
            />
          </div>
          <div className="p-3 rounded-lg bg-cyan-50 border border-cyan-100">
            <p className="text-[10px] uppercase tracking-wider text-cyan-400 mb-1.5">Marketing Price</p>
            <Input
              data-testid="marketing-price"
              type="number"
              value={mp || ''}
              onChange={e => onUpdate('marketing_price', parseFloat(e.target.value) || 0)}
              placeholder="$0"
              className="h-8 text-sm font-mono font-semibold bg-white border-cyan-200 text-cyan-900"
            />
          </div>
          <div className="p-3 rounded-lg bg-amber-50 border border-amber-100">
            <p className="text-[10px] uppercase tracking-wider text-amber-400 mb-1.5">Sold Price</p>
            <Input
              data-testid="sold-price"
              type="number"
              value={sp || ''}
              onChange={e => onUpdate('sold_price', parseFloat(e.target.value) || 0)}
              placeholder="$0"
              className="h-8 text-sm font-mono font-semibold bg-white border-amber-200 text-amber-900"
            />
          </div>
          <div className={`p-3 rounded-lg border ${calcFee > 0 ? 'bg-emerald-50 border-emerald-100' : calcFee < 0 ? 'bg-red-50 border-red-100' : 'bg-gray-50 border-gray-100'}`}>
            <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">Assignment Fee</p>
            <p data-testid="calculated-assignment-fee" className={`text-lg font-bold font-mono ${calcFee > 0 ? 'text-emerald-600' : calcFee < 0 ? 'text-red-600' : 'text-gray-400'}`}>
              {calcFee !== 0 ? fmt(calcFee) : '--'}
            </p>
            <p className="text-[9px] text-gray-400 mt-0.5">
              {sp > 0 ? 'Sold - Contract' : mp > 0 ? 'Marketing - Contract' : 'Set prices above'}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/* ─── Quick Actions Panel ─── */

/* ─── Activity Timeline ─── */
function ActivityTimeline({ activities, onAddNote }) {
  const [note, setNote] = useState('');
  const [noteType, setNoteType] = useState('note');

  const handleAdd = () => {
    if (!note.trim()) return;
    onAddNote(noteType, note.trim());
    setNote('');
  };

  return (
    <Card data-testid="activity-timeline" className="bg-white border-gray-200 shadow-sm">
      <CardContent className="p-5">
        <SectionHeader title="Activity Timeline" icon={Activity} />

        {/* Add Note */}
        <div className="flex gap-2 mb-5">
          <Select value={noteType} onValueChange={setNoteType}>
            <SelectTrigger data-testid="activity-type-select" className={`w-32 ${inputClass}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white border-gray-200">
              <SelectItem value="note">Note</SelectItem>
              <SelectItem value="call">Call</SelectItem>
              <SelectItem value="follow_up">Follow-Up</SelectItem>
            </SelectContent>
          </Select>
          <Input
            data-testid="activity-note-input"
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="Log a note, call, or follow-up..."
            className={`flex-1 ${inputClass}`}
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
          />
          <Button
            data-testid="add-activity-btn"
            onClick={handleAdd}
            disabled={!note.trim()}
            size="sm"
            className="bg-[#0073ea] hover:bg-[#0060c2] text-white px-4"
          >
            Add
          </Button>
        </div>

        {/* Timeline */}
        <div className="space-y-0">
          {activities.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">No activity yet</p>
          ) : (
            activities.map((a, i) => {
              const Icon = activityIcons[a.activity_type] || Activity;
              const cfg = a.activity_type === 'status_change' ? stageConfig[a.metadata?.new_status] : null;
              return (
                <div
                  key={a.id}
                  data-testid={`activity-item-${a.id}`}
                  className="flex gap-3 py-3 group"
                >
                  <div className="flex flex-col items-center">
                    <div className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${
                      cfg ? cfg.bg : 'bg-gray-100'
                    }`}>
                      <Icon className={`h-3.5 w-3.5 ${cfg ? cfg.text : 'text-gray-500'}`} />
                    </div>
                    {i < activities.length - 1 && <div className="w-px flex-1 bg-gray-200 mt-1" />}
                  </div>
                  <div className="flex-1 min-w-0 pb-1">
                    <p className="text-sm text-gray-800">{a.description}</p>
                    <p className="text-[11px] text-gray-400 mt-0.5">
                      {new Date(a.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/* ─── Property Links ─── */
function PropertyLinks({ lead }) {
  const street = lead.property_address || '';
  const city = lead.property_city || '';
  const state = lead.property_state || '';
  const zip = lead.property_zip || '';
  const addr = [street, city, state, zip].filter(Boolean).join(', ');
  const county = (lead.county || '').toLowerCase();

  if (!addr) return null;

  const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`;

  // Zillow — address search
  const zillowUrl = `https://www.zillow.com/homes/${encodeURIComponent(street + ', ' + city + ', ' + state + ' ' + zip)}_rb/`;

  // County Property Appraiser — folio-based direct lookup
  let appraiserUrl = null;
  if (lead.folio) {
    const folioRaw = lead.folio.replace(/-/g, '');
    const folioFormatted = folioRaw.length === 13
      ? `${folioRaw.slice(0,2)}-${folioRaw.slice(2,6)}-${folioRaw.slice(6,9)}-${folioRaw.slice(9,13)}`
      : lead.folio;
    if (county.includes('miami') || county.includes('dade')) {
      appraiserUrl = `https://apps.miamidadepa.gov/propertysearch/#/?search=${folioFormatted}`;
    } else if (county.includes('broward')) {
      appraiserUrl = `https://web.bcpa.net/BcpaClient/#/Record-Detail/${folioRaw}`;
    } else if (county.includes('palm')) {
      appraiserUrl = `https://www.pbcgov.org/papa/Asp/PropertyDetail/PropertyDetail.aspx?parcel=${lead.folio}`;
    } else {
      appraiserUrl = `https://www.google.com/search?q=${encodeURIComponent((lead.county || '') + ' county property appraiser folio ' + lead.folio)}`;
    }
  } else {
    appraiserUrl = `https://www.google.com/search?q=${encodeURIComponent((lead.county || 'county') + ' property appraiser ' + street + ' ' + city)}`;
  }

  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
  const redirectUrl = (url) => `${API_BASE}/api/redirect?url=${encodeURIComponent(url)}`;

  const openGoogleMaps = (e) => {
    e.preventDefault();
    // Try navigating the top-level window (escapes iframe sandbox)
    try {
      window.top.location.href = googleMapsUrl;
      return;
    } catch (_) { /* cross-origin — try next */ }
    // Fallback: standard window.open
    const w = window.open(googleMapsUrl, '_blank', 'noopener,noreferrer');
    if (!w) {
      // Last resort: navigate current window
      window.location.href = googleMapsUrl;
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 mt-3">
      <a href={googleMapsUrl} target="_blank" rel="noopener noreferrer" onClick={openGoogleMaps} data-testid="link-google-maps"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 transition-colors cursor-pointer">
        <MapPin className="h-3.5 w-3.5" /> Google Maps
        <ExternalLink className="h-3 w-3 opacity-50" />
      </a>
      <a href={zillowUrl} target="_blank" rel="noopener noreferrer" data-testid="link-zillow"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors cursor-pointer">
        <Home className="h-3.5 w-3.5" /> Zillow
        <ExternalLink className="h-3 w-3 opacity-50" />
      </a>
      <a href={redirectUrl(appraiserUrl)} target="_blank" rel="noopener noreferrer" data-testid="link-appraiser"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-colors cursor-pointer">
        <Scale className="h-3.5 w-3.5" /> County Appraiser
        <ExternalLink className="h-3 w-3 opacity-50" />
      </a>
    </div>
  );
}

/* ─── DD Deadline Banner (compact) ─── */
function DdBanner({ ddDeadline }) {
  const deadline = new Date(ddDeadline);
  const now = new Date();
  const daysLeft = Math.ceil((deadline - now) / 86400000);
  const isExpired = daysLeft < 0;
  const isUrgent = daysLeft >= 0 && daysLeft <= 3;
  const isWarning = daysLeft > 3 && daysLeft <= 7;

  const dateStr = deadline.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  const statusText = isExpired
    ? `Expired ${Math.abs(daysLeft)} day${Math.abs(daysLeft) !== 1 ? 's' : ''} ago`
    : `${daysLeft} day${daysLeft !== 1 ? 's' : ''} left`;

  const bg = isExpired ? 'bg-red-50 border-red-200' : isUrgent ? 'bg-red-50 border-red-200' : isWarning ? 'bg-amber-50 border-amber-200' : 'bg-indigo-50 border-indigo-200';
  const textColor = isExpired ? 'text-red-700' : isUrgent ? 'text-red-700' : isWarning ? 'text-amber-700' : 'text-indigo-700';
  const iconColor = isExpired ? 'text-red-500' : isUrgent ? 'text-red-500' : isWarning ? 'text-amber-500' : 'text-indigo-500';

  return (
    <div data-testid="dd-deadline-banner" className={`flex items-center gap-3 px-4 py-2.5 rounded-lg border ${bg}`}>
      <Clock className={`h-4 w-4 shrink-0 ${iconColor}`} />
      <span className={`text-sm font-medium ${textColor}`}>
        Due Diligence Deadline: {dateStr}
      </span>
      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${isExpired || isUrgent ? 'bg-red-100 text-red-700' : isWarning ? 'bg-amber-100 text-amber-700' : 'bg-indigo-100 text-indigo-700'}`}>
        {statusText}
      </span>
    </div>
  );
}

/* ─── Main Page ─── */
export default function LeadDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [emails, setEmails] = useState([]);
  const [activities, setActivities] = useState([]);

  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [generating, setGenerating] = useState(false);

  const [emailForm, setEmailForm] = useState({ to_email: '', subject: '', html_content: '', contract_ids: [], send_copy: true });
  const [sending, setSending] = useState(false);
  const [selectedEmailTemplate, setSelectedEmailTemplate] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Sections visibility (controlled by quick actions)
  const [showContractSection, setShowContractSection] = useState(true);
  const [showEmailSection, setShowEmailSection] = useState(false);

  // Send to Buyers state
  const [buyers, setBuyers] = useState([]);
  const [blastPreselect, setBlastPreselect] = useState(null);

  // Blast history refresh trigger
  const [blastRefreshKey, setBlastRefreshKey] = useState(0);

  // Gmail OAuth state
  const [gmailStatus, setGmailStatus] = useState({ connected: false, email: '' });

  const fetchGmailStatus = useCallback(async () => {
    try {
      const res = await API.get('/oauth/gmail/status');
      setGmailStatus(res.data);
    } catch {}
  }, []);

  // Send to Wholesalers state
  const [wholesalers, setWholesalers] = useState([]);

  // Preview state
  const [showPreview, setShowPreview] = useState(false);
  const [previewPdf, setPreviewPdf] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewPages, setPreviewPages] = useState(0);
  const [previewPage, setPreviewPage] = useState(1);
  const [annotations, setAnnotations] = useState([]);
  const [flyerOpen, setFlyerOpen] = useState(false);
  const pageAnnotations = useMemo(() => annotations.filter(a => a.page_num === previewPage - 1), [annotations, previewPage]);
  const previewContainerRef = useRef(null);
  const [previewCanvasSize, setPreviewCanvasSize] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const dragOffsetRef = useRef({ x: 0, y: 0 });

  // Sold price prompt state
  const [showSoldPriceDialog, setShowSoldPriceDialog] = useState(false);
  const [pendingSoldPrice, setPendingSoldPrice] = useState('');
  const [pendingSoldStatus, setPendingSoldStatus] = useState('Closed');

  const fetchLead = useCallback(async () => {
    try {
      const res = await API.get(`/leads/${id}`);
      const data = res.data;
      // Auto-fill date_signed with today's date if empty
      if (!data.date_signed) {
        const today = new Date();
        data.date_signed = `${String(today.getMonth() + 1).padStart(2, '0')}/${String(today.getDate()).padStart(2, '0')}/${today.getFullYear()}`;
      }
      setLead(data);
      setEmailForm(prev => ({ ...prev, to_email: data.agent_email || '' }));
    } catch { navigate('/leads'); } finally { setLoading(false); }
  }, [id, navigate]);

  const fetchRelated = useCallback(async () => {
    const [tRes, etRes, cRes, eRes, bRes, aRes, wRes] = await Promise.all([
      API.get('/templates').catch(() => ({ data: [] })),
      API.get('/email-templates').catch(() => ({ data: [] })),
      API.get('/contracts', { params: { lead_id: id } }).catch(() => ({ data: [] })),
      API.get('/emails', { params: { lead_id: id } }).catch(() => ({ data: [] })),
      API.get('/buyers').catch(() => ({ data: [] })),
      API.get(`/leads/${id}/activities`).catch(() => ({ data: [] })),
      API.get('/wholesalers').catch(() => ({ data: [] })),
    ]);
    setTemplates(tRes.data);
    setEmailTemplates(etRes.data);
    setContracts(cRes.data);
    setEmails(eRes.data);
    setBuyers(bRes.data);
    setActivities(aRes.data);
    setWholesalers(wRes.data);
  }, [id]);

  useEffect(() => { fetchLead(); fetchRelated(); fetchGmailStatus(); }, [fetchLead, fetchRelated, fetchGmailStatus]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await API.put(`/leads/${id}`, lead);
      setLead(res.data);
      toast.success('Lead saved');
    } catch { toast.error('Failed to save'); } finally { setSaving(false); }
  };

  const handleStageChange = async (status) => {
    if (status === lead.status) return;
    // If changing to Closed or Sold, prompt for sold price first
    if (status === 'Closed' || status === 'Sold') {
      setPendingSoldPrice(lead.sold_price || '');
      setPendingSoldStatus(status);
      setShowSoldPriceDialog(true);
      return;
    }
    try {
      const res = await API.put(`/leads/${id}`, { status });
      setLead(res.data);
      toast.success(`Status: ${status}`);
      fetchRelated();
    } catch { toast.error('Failed to update status'); }
  };

  const handleConfirmSoldPrice = async () => {
    try {
      const sp = parseFloat(pendingSoldPrice) || 0;
      const res = await API.put(`/leads/${id}`, { status: pendingSoldStatus || 'Closed', sold_price: sp });
      setLead(res.data);
      setShowSoldPriceDialog(false);
      toast.success(pendingSoldStatus === 'Sold' ? 'Deal sold! Sold price recorded.' : 'Deal closed! Sold price recorded.');
      fetchRelated();
    } catch { toast.error('Failed to update'); }
  };

  const handleGenerate = async () => {
    if (!selectedTemplate) { toast.error('Select a template'); return; }
    setGenerating(true);
    try {
      const res = await API.post('/contracts/generate', { lead_id: id, template_id: selectedTemplate, field_values: {}, annotations });
      toast.success(`Contract ${res.data.contract_ref} generated`);
      setAnnotations([]);
      setShowPreview(false);
      fetchRelated();
    } catch (err) { toast.error(err.response?.data?.detail || 'Generation failed'); } finally { setGenerating(false); }
  };

  const handleDownloadContract = async (contractId, contractName) => {
    const safeName = (contractName || 'contract').replace(/[^a-zA-Z0-9\s_-]/g, '').replace(/\s+/g, '_');
    const filename = `${safeName}.pdf`;

    try {
      // Step 1: Open native save dialog IMMEDIATELY while user gesture is active
      let fileHandle = null;
      if (window.showSaveFilePicker) {
        try {
          fileHandle = await window.showSaveFilePicker({
            suggestedName: filename,
            types: [{ description: 'PDF Files', accept: { 'application/pdf': ['.pdf'] } }],
          });
        } catch (e) {
          if (e.name === 'AbortError') return; // User cancelled
          // showSaveFilePicker not available or failed — will fallback below
        }
      }

      // Step 2: Fetch the contract PDF data
      toast.info('Downloading contract...');
      const res = await API.get(`/contracts/${contractId}`);
      const pdfData = res.data.pdf_data;
      if (!pdfData) { toast.error('No PDF data found'); return; }

      // Step 3: Convert base64 to blob
      const byteCharacters = atob(pdfData);
      const byteArray = new Uint8Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteArray[i] = byteCharacters.charCodeAt(i);
      }
      const blob = new Blob([byteArray], { type: 'application/pdf' });

      // Step 4: Write to file handle or fallback
      if (fileHandle) {
        const writable = await fileHandle.createWritable();
        await writable.write(blob);
        await writable.close();
        toast.success('Contract saved!');
      } else {
        // Fallback: blob URL + link click
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
          URL.revokeObjectURL(blobUrl);
        }, 200);
        toast.success('Download started');
      }
    } catch (err) {
      console.error('Download failed:', err);
      toast.error('Download failed — please try again');
    }
  };

  const handleSelectEmailTemplate = (templateId) => {
    setSelectedEmailTemplate(templateId);
    const tmpl = emailTemplates.find(t => t.id === templateId);
    if (tmpl && lead) {
      let html = tmpl.html_content;
      let subject = tmpl.subject;
      const replacements = {
        '{{agent_name}}': lead.agent_name || '',
        '{{agent_first_name}}': (lead.agent_name || '').split(' ')[0] || '',
        '{{buyer_name}}': lead.buyer_name || '',
        '{{property_address}}': lead.property_address || '',
        '{{property_street}}': lead.property_address || '',
        '{{offer_amount}}': lead.offer_amount ? `$${lead.offer_amount.toLocaleString()}` : '',
        '{{escrow_deposit}}': lead.escrow_deposit || '',
        '{{inspection_time}}': lead.inspection_time || '',
        '{{closing_date}}': lead.closing_date || '',
        '{{lead_name}}': lead.name || '',
        '{{seller_name}}': lead.seller_name || '',
      };
      Object.entries(replacements).forEach(([k, v]) => {
        html = html.replace(new RegExp(k.replace(/[{}]/g, '\\$&'), 'g'), v);
        subject = subject.replace(new RegExp(k.replace(/[{}]/g, '\\$&'), 'g'), v);
      });
      setEmailForm(prev => ({ ...prev, subject, html_content: html }));
    }
  };

  const handleSendEmail = async () => {
    if (!emailForm.to_email || !emailForm.subject) { toast.error('Fill in email and subject'); return; }
    setSending(true);
    try {
      const payload = {
        lead_id: id,
        to_email: emailForm.to_email,
        subject: emailForm.subject,
        html_content: emailForm.html_content,
        contract_ids: emailForm.contract_ids,
        uploaded_file_ids: uploadedFiles.map(f => f.id),
      };
      await API.post('/emails/send', payload);
      // Send copy to self if checked
      if (emailForm.send_copy) {
        try {
          const me = await API.get('/auth/me');
          if (me.data.email && me.data.email !== emailForm.to_email) {
            await API.post('/emails/send', { ...payload, to_email: me.data.email });
          }
        } catch { /* ignore copy failure */ }
      }
      toast.success('Email sent');
      setEmailForm({ to_email: lead?.agent_email || '', subject: '', html_content: '', contract_ids: [], send_copy: true });
      setSelectedEmailTemplate('');
      setUploadedFiles([]);
      fetchRelated();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send'); } finally { setSending(false); }
  };

  const handleUploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await API.post('/uploads/temp', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUploadedFiles(prev => [...prev, { id: res.data.id, filename: res.data.filename, size: res.data.size }]);
      toast.success(`${file.name} uploaded`);
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const handleRemoveUpload = async (fileId) => {
    try {
      await API.delete(`/uploads/temp/${fileId}`);
      setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
    } catch { setUploadedFiles(prev => prev.filter(f => f.id !== fileId)); }
  };

  const toggleContractAttachment = (contractId) => {
    setEmailForm(prev => ({
      ...prev,
      contract_ids: prev.contract_ids.includes(contractId)
        ? prev.contract_ids.filter(cid => cid !== contractId)
        : [...prev.contract_ids, contractId]
    }));
  };

  const handleDeleteContract = async (contractId) => {
    try {
      await API.delete(`/contracts/${contractId}`);
      toast.success('Contract deleted');
      fetchRelated();
    } catch { toast.error('Failed to delete contract'); }
  };

  const handleBlastSendComplete = () => {
    fetchRelated();
    setBlastRefreshKey(k => k + 1);
  };

  const handlePreview = async () => {
    if (!selectedTemplate) { toast.error('Select a template first'); return; }
    setPreviewLoading(true);
    try {
      const res = await API.post('/contracts/preview', { template_id: selectedTemplate, lead_id: id, field_values: {} });
      setPreviewPdf(res.data.pdf_data);
      setPreviewPage(1);
      setAnnotations([]);
      setShowPreview(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
    } finally { setPreviewLoading(false); }
  };

  const handleAddActivity = async (type, description) => {
    try {
      await API.post(`/leads/${id}/activities`, { activity_type: type, description });
      toast.success('Activity logged');
      fetchRelated();
    } catch { toast.error('Failed to log activity'); }
  };



  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64 bg-gray-200" />
        <Skeleton className="h-16 bg-gray-200 rounded-xl" />
        <Skeleton className="h-[300px] bg-gray-200 rounded-xl" />
      </div>
    );
  }

  if (!lead) return null;

  const update = (field, value) => setLead(prev => ({ ...prev, [field]: value }));
  const cfg = stageConfig[lead.status] || stageConfig['New'];
  const flyerLead = { id: lead.id };
  const flyerProperty = { property_address: lead.property_address || lead.name, property_city: lead.property_city || '', property_state: lead.property_state || 'FL',
    property_zip: lead.property_zip || '', asking_price: lead.marketing_price || lead.asking_price || 0, property_type: lead.property_type || '' };

  return (
    <div data-testid="lead-detail-page" className="space-y-5 max-w-5xl">
      {/* Header — compact single row */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <Button variant="ghost" size="icon" onClick={() => navigate('/leads')} data-testid="back-to-leads" className="text-gray-500 hover:text-gray-900 shrink-0 h-8 w-8">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="min-w-0 flex-1 sm:flex-none text-lg font-bold text-gray-900 truncate">{lead.property_address || lead.name}</h1>
        <Badge variant="outline" className={`border ${cfg.badgeBg} ${cfg.text} ${cfg.border} shrink-0`}>{lead.status}</Badge>
        <div className="w-full sm:w-auto sm:ml-auto shrink-0 flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setFlyerOpen(true)} className="h-9 shrink-0 border-violet-400/40 text-violet-300" data-testid="lead-flyer-btn"><FileImage className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Deal Flyer</span></Button>
          <StatusStepper currentStatus={lead.status} onStatusChange={handleStageChange} />
        </div>
      </div>

      {/* DD Deadline Banner (compact, only shows when relevant) */}
      {lead.dd_deadline && (
        <DdBanner ddDeadline={lead.dd_deadline} />
      )}

      {/* Deal Summary + Quick Actions (side by side on large screens) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2">
          <DealSummary lead={lead} onUpdate={update} />
        </div>
        <LeadUpdates leadId={lead.id} />
      </div>

      <HotMatchBanner lead={lead} />
      <FlyerDialog open={flyerOpen} onOpenChange={setFlyerOpen} lead={flyerLead} property={flyerProperty} />

      {/* ────── PROPERTY DETAILS ────── */}
      <Card className="bg-white border-gray-200 shadow-sm">
        <CardContent className="p-6">
          <SectionHeader title="Property Details" icon={Home} />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="col-span-2">
              <Field label="Property Address">
                <AddressAutocomplete
                  data-testid="prop-address"
                  value={lead.property_address || ''}
                  onChange={v => update('property_address', v)}
                  onPlaceSelect={place => {
                    setLead(prev => ({
                      ...prev,
                      property_address: place.street,
                      property_city: place.city,
                      property_state: place.state,
                      property_zip: place.zip,
                      county: place.county || prev.county,
                    }));
                  }}
                  placeholder="Start typing address..."
                  className={inputClass}
                />
              </Field>
            </div>
            <Field label="City">
              <Input value={lead.property_city || ''} onChange={e => update('property_city', e.target.value)} placeholder="City" className={inputClass} />
            </Field>
            <Field label="State / ZIP">
              <div className="flex gap-2">
                <Input value={lead.property_state || ''} onChange={e => update('property_state', e.target.value)} placeholder="FL" className={`${inputClass} w-14 shrink-0 px-2 sm:w-20 sm:px-3`} />
                <Input value={lead.property_zip || ''} onChange={e => update('property_zip', e.target.value)} placeholder="33101" className={`${inputClass} min-w-0 flex-1 px-2 sm:px-3`} />
              </div>
            </Field>
            <Field label="Folio Number">
              <Input data-testid="prop-folio" value={lead.folio || ''} onChange={e => update('folio', e.target.value)} placeholder="Folio #" className={inputClass} />
            </Field>
            <Field label="County">
              <Input data-testid="prop-county" value={lead.county || ''} onChange={e => update('county', e.target.value)} placeholder="e.g. Miami-Dade" className={inputClass} />
            </Field>
            <Field label="Bedrooms">
              <Input type="number" value={lead.bedrooms || ''} onChange={e => update('bedrooms', parseInt(e.target.value) || 0)} className={inputClass} />
            </Field>
            <Field label="Bathrooms">
              <Input type="number" value={lead.bathrooms || ''} onChange={e => update('bathrooms', parseInt(e.target.value) || 0)} className={inputClass} />
            </Field>
            <Field label="Sq Ft">
              <Input type="number" value={lead.sqft || ''} onChange={e => update('sqft', parseInt(e.target.value) || 0)} className={inputClass} />
            </Field>
            <Field label="Lot Size (sq ft)">
              <Input data-testid="prop-lot-size" type="number" value={lead.lot_size || ''} onChange={e => update('lot_size', parseInt(e.target.value) || 0)} placeholder="e.g. 7500" className={inputClass} />
            </Field>
            <Field label="Year Built">
              <Input data-testid="prop-year-built" type="number" value={lead.year_built || ''} onChange={e => update('year_built', parseInt(e.target.value) || 0)} placeholder="e.g. 1985" className={inputClass} />
            </Field>
            <Field label="MLS Number">
              <Input data-testid="prop-mls" value={lead.mls_number || ''} onChange={e => update('mls_number', e.target.value)} placeholder="MLS #" className={inputClass} />
            </Field>
            <Field label="Days on Market (DOM)">
              <Input data-testid="prop-dom" type="number" value={lead.dom ?? ''} onChange={e => update('dom', e.target.value === '' ? null : parseInt(e.target.value) || 0)} placeholder="0" className={inputClass} />
            </Field>
            <Field label="Asking Price ($)">
              <Input data-testid="asking-price" type="number" value={lead.asking_price || ''} onChange={e => update('asking_price', parseFloat(e.target.value) || 0)} placeholder="0" className={inputClass} />
            </Field>
            <Field label="ARV ($)">
              <Input data-testid="arv-input" type="number" value={lead.arv || ''} onChange={e => update('arv', parseFloat(e.target.value) || 0)} placeholder="After Repair Value" className={inputClass} />
            </Field>
            <Field label="Repair Estimate ($)">
              <Input data-testid="repair-estimate" type="number" value={lead.repair_estimate || ''} onChange={e => update('repair_estimate', parseFloat(e.target.value) || 0)} placeholder="0" className={inputClass} />
            </Field>
            <div className="col-span-2 md:col-span-4">
              <Field label="Legal Description">
                <Textarea data-testid="prop-legal" value={lead.legal_description || ''} onChange={e => update('legal_description', e.target.value)} rows={2} placeholder="Legal description of the property..." className={`${inputClass} resize-none`} />
              </Field>
            </div>
          </div>
          <PropertyLinks lead={lead} />
        </CardContent>
      </Card>

      {/* ────── CONTRACT INFO (unified: fields + generate + email to agent) ────── */}
      <Card className="bg-white border-gray-200 shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <SectionHeader title="Contract Info" icon={FileSignature} />
            <Button onClick={handleSave} disabled={saving} data-testid="save-lead-btn" className="bg-[#0073ea] hover:bg-[#0060c2] text-white">
              <Save className="h-4 w-4 mr-2" /> {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Field label="Seller's Name">
              <Input data-testid="seller-name" value={lead.seller_name || ''} onChange={e => update('seller_name', e.target.value)} placeholder="Seller's full name" className={inputClass} />
            </Field>
            <Field label="Offer Amount ($)">
              <Input data-testid="offer-amount" type="number" value={lead.offer_amount || ''} onChange={e => update('offer_amount', parseFloat(e.target.value) || 0)} placeholder="0" className={inputClass} />
            </Field>
            <Field label="Escrow Deposit">
              <Input data-testid="escrow-deposit" value={lead.escrow_deposit || ''} onChange={e => update('escrow_deposit', e.target.value)} placeholder="e.g. $5,000" className={inputClass} />
            </Field>
            <Field label="Closing Date">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    data-testid="closing-date"
                    className={`w-full justify-start text-left font-normal ${inputClass} ${!lead.closing_date ? 'text-gray-400' : 'text-gray-900'}`}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4 text-gray-400" />
                    {lead.closing_date || 'Select closing date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-white border-gray-200" align="start">
                  <Calendar
                    mode="single"
                    selected={lead.closing_date ? new Date(lead.closing_date + 'T00:00:00') : undefined}
                    onSelect={(date) => {
                      if (date) {
                        const formatted = `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}/${date.getFullYear()}`;
                        update('closing_date', formatted);
                      }
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </Field>
            <Field label="Inspection Time">
              <Input data-testid="inspection-time" value={lead.inspection_time || ''} onChange={e => update('inspection_time', e.target.value)} placeholder="e.g. 10 days" className={inputClass} />
            </Field>
            <Field label="Assignability">
              <Input data-testid="assignability" value={lead.assignability || ''} onChange={e => update('assignability', e.target.value)} placeholder="e.g. Assignable" className={inputClass} />
            </Field>
            <Field label="Closing Costs">
              <Input data-testid="closing-costs" value={lead.closing_costs || ''} onChange={e => update('closing_costs', e.target.value)} placeholder="e.g. Buyer pays" className={inputClass} />
            </Field>
            <Field label="Folio Number">
              <Input value={lead.folio || ''} onChange={e => update('folio', e.target.value)} placeholder="Folio #" className={inputClass} />
            </Field>
            <Field label="Date Signed">
              <Input data-testid="date-signed" value={lead.date_signed || ''} onChange={e => update('date_signed', e.target.value)} placeholder="e.g. 03/18/2026" className={inputClass} />
            </Field>
            <div className="md:col-span-2 lg:col-span-3">
              <Field label="Additional Terms">
                <Textarea data-testid="additional-terms" value={lead.additional_terms || ''} onChange={e => update('additional_terms', e.target.value)} rows={2} placeholder="Any additional terms..." className={`${inputClass} resize-none`} />
              </Field>
            </div>

            <Separator className="md:col-span-2 lg:col-span-3 bg-gray-200 my-1" />

            <Field label="Listing Agent Name">
              <Input data-testid="agent-name" value={lead.agent_name || ''} onChange={e => update('agent_name', e.target.value)} placeholder="Agent name" className={inputClass} />
            </Field>
            <Field label="Listing Broker">
              <Input data-testid="listing-broker" value={lead.listing_broker || ''} onChange={e => update('listing_broker', e.target.value)} placeholder="Broker name" className={inputClass} />
            </Field>
            <Field label="Agent Email">
              <Input data-testid="agent-email" type="email" value={lead.agent_email || ''} onChange={e => update('agent_email', e.target.value)} placeholder="agent@email.com" className={inputClass} />
            </Field>
            <Field label="Agent Phone">
              <Input data-testid="agent-phone" value={lead.agent_phone || ''} onChange={e => update('agent_phone', e.target.value)} placeholder="(555) 123-4567" className={inputClass} />
            </Field>
          </div>

          {/* ── Generate Contract ── */}
          <Separator className="bg-gray-200 my-5" />
          <div id="contract-section">
            <p className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-4"><FileSignature className="h-4 w-4 text-purple-500" /> Generate Contract</p>

            <div className="flex flex-wrap items-end gap-3 mb-4">
                  <div className="w-full sm:w-auto sm:flex-1">
                    <Field label="Contract Template">
                      <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                        <SelectTrigger data-testid="contract-template-select" className={inputClass}>
                          <SelectValue placeholder="Select template" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-gray-200">
                          {templates.map(t => <SelectItem key={t.id} value={t.id} className="text-gray-900">{t.name} ({t.fields?.length || 0} fields)</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </Field>
                  </div>
                  <Button
                    data-testid="preview-contract-btn"
                    onClick={handlePreview}
                    disabled={previewLoading || !selectedTemplate}
                    variant="outline"
                    className="border-gray-300 text-gray-700 hover:bg-gray-100"
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    {previewLoading ? 'Loading...' : 'Preview'}
                  </Button>
                  <Button
                    data-testid="generate-contract-btn"
                    onClick={handleGenerate}
                    disabled={generating || !selectedTemplate}
                    className="bg-[#0073ea] hover:bg-[#0060c2] text-white"
                  >
                    <FileSignature className="h-4 w-4 mr-2" />
                    {generating ? 'Generating...' : 'Generate'}
                  </Button>
                </div>
                {templates.length === 0 && <p className="text-xs text-gray-500">No templates yet. Upload one in Contract Templates.</p>}

            {contracts.length > 0 && (
              <>
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-3">Generated Contracts</p>
                <div className="space-y-2">
                  {contracts.map(c => (
                    <div key={c.id} data-testid={`contract-${c.id}`} className="flex items-center justify-between p-3 rounded-lg bg-[#f6f7fb] border border-gray-200">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 truncate">{c.contract_name || c.contract_ref}</p>
                        <p className="text-xs text-gray-500">{c.template_name} - {new Date(c.created_at).toLocaleString()}</p>
                      </div>
                      <div className="flex items-center gap-1 ml-3 shrink-0">
                        <button
                          onClick={() => handleDownloadContract(c.id, c.contract_name || c.contract_ref)}
                          data-testid={`download-contract-${c.id}`}
                          className="inline-flex items-center justify-center h-8 px-2.5 rounded-md text-xs text-[#0073ea] hover:text-[#0060c2] hover:bg-blue-50 transition-colors cursor-pointer"
                        >
                          <Download className="h-3.5 w-3.5 mr-1" /> Download
                        </button>
                        <button
                          onClick={() => handleDeleteContract(c.id)}
                          data-testid={`delete-contract-${c.id}`}
                          className="inline-flex items-center justify-center h-8 w-8 rounded-md text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* ── Send Email to Agent ── */}
          <Separator className="bg-gray-200 my-5" />
          <div id="email-section">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm font-semibold text-gray-700 flex items-center gap-2"><Mail className="h-4 w-4 text-blue-500" /> Send Email to Agent</p>
              <Button variant="ghost" size="sm" onClick={() => setShowEmailSection(!showEmailSection)} className="text-[#0073ea]">
                {showEmailSection ? 'Collapse' : 'Expand'}
              </Button>
            </div>

            {/* Gmail connection status */}
            {showEmailSection && (
              <div className={`flex items-center justify-between px-3 py-2.5 rounded-lg border mb-4 ${gmailStatus.connected ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
                <div className="flex items-center gap-2 text-sm">
                  <svg className="h-4 w-4" viewBox="0 0 24 24"><path fill={gmailStatus.connected ? "#16a34a" : "#d97706"} d="M20 18h-2V9.25L12 13 6 9.25V18H4V6h1.2l6.8 4.25L18.8 6H20m0-2H4c-1.11 0-2 .89-2 2v12a2 2 0 002 2h16a2 2 0 002-2V6a2 2 0 00-2-2z"/></svg>
                  {gmailStatus.connected ? (
                    <span className="text-emerald-700">Sending from <strong>{gmailStatus.email}</strong></span>
                  ) : (
                    <span className="text-amber-700">Connect Gmail to send emails from your own account</span>
                  )}
                </div>
                {gmailStatus.connected ? (
                  <button
                    data-testid="disconnect-gmail-btn"
                    onClick={async () => {
                      try {
                        await API.post('/oauth/gmail/disconnect');
                        setGmailStatus({ connected: false, email: '' });
                        toast.success('Gmail disconnected');
                      } catch { toast.error('Failed to disconnect'); }
                    }}
                    className="text-xs text-red-500 hover:text-red-700 font-medium"
                  >
                    Disconnect
                  </button>
                ) : (
                  <button
                    data-testid="connect-gmail-btn"
                    onClick={async () => {
                      try {
                        const res = await API.get('/oauth/gmail/start');
                        window.open(res.data.auth_url, '_blank', 'noopener');
                      } catch { toast.error('Failed to start Gmail connection'); }
                    }}
                    className="text-xs bg-[#0073ea] text-white px-3 py-1.5 rounded-md hover:bg-[#0060c2] font-medium"
                  >
                    Connect Gmail
                  </button>
                )}
              </div>
            )}

            {showEmailSection && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Email Template (optional)">
                    <Select value={selectedEmailTemplate} onValueChange={handleSelectEmailTemplate}>
                      <SelectTrigger data-testid="email-template-select" className={inputClass}>
                        <SelectValue placeholder="Start from template..." />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-gray-200">
                        {emailTemplates.map(t => <SelectItem key={t.id} value={t.id} className="text-gray-900">{t.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="To">
                    <Input data-testid="email-to" type="email" value={emailForm.to_email} onChange={e => setEmailForm(prev => ({ ...prev, to_email: e.target.value }))} className={inputClass} placeholder="agent@email.com" />
                  </Field>
                </div>
                <Field label="Subject">
                  <Input data-testid="email-subject" value={emailForm.subject} onChange={e => setEmailForm(prev => ({ ...prev, subject: e.target.value }))} className={inputClass} placeholder="Offer for property" />
                </Field>
                <Field label="Body (HTML)">
                  <Textarea data-testid="email-body" value={emailForm.html_content} onChange={e => setEmailForm(prev => ({ ...prev, html_content: e.target.value }))} rows={5} className={`${inputClass} resize-none font-mono text-xs`} placeholder="<p>Dear {{agent_name}},</p>" />
                </Field>
                <div className="space-y-3">
                  {contracts.length > 0 && (
                    <div>
                      <Label className="text-gray-500 text-xs uppercase tracking-wider mb-2 block">Attach Contracts</Label>
                      <div className="space-y-1.5 max-h-36 overflow-y-auto">
                        {contracts.map(c => (
                          <label
                            key={c.id}
                            data-testid={`attach-contract-${c.id}`}
                            className={`flex items-center gap-2.5 px-3 py-2 rounded-lg cursor-pointer transition-colors text-sm ${
                              emailForm.contract_ids.includes(c.id) ? 'bg-blue-50 border border-blue-200' : 'bg-[#f6f7fb] border border-gray-100 hover:border-gray-300'
                            }`}
                          >
                            <Checkbox
                              checked={emailForm.contract_ids.includes(c.id)}
                              onCheckedChange={() => toggleContractAttachment(c.id)}
                            />
                            <Paperclip className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                            <span className="truncate text-gray-700">{c.contract_name || c.template_name}</span>
                            <span className="text-[10px] text-gray-400 ml-auto shrink-0">{new Date(c.created_at).toLocaleDateString()}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <Label className="text-gray-500 text-xs uppercase tracking-wider mb-2 block">Upload File</Label>
                    <label
                      data-testid="upload-attachment-btn"
                      className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-dashed border-gray-300 text-sm text-gray-500 hover:border-[#0073ea] hover:text-[#0073ea] transition-colors cursor-pointer"
                    >
                      <Upload className="h-4 w-4" />
                      {uploading ? 'Uploading...' : 'Choose file from computer'}
                      <input type="file" className="hidden" onChange={handleUploadFile} disabled={uploading} />
                    </label>
                    {uploadedFiles.length > 0 && (
                      <div className="space-y-1.5 mt-2">
                        {uploadedFiles.map(f => (
                          <div key={f.id} data-testid={`uploaded-file-${f.id}`} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200 text-sm">
                            <Paperclip className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                            <span className="truncate text-emerald-700">{f.filename}</span>
                            <span className="text-[10px] text-emerald-400 ml-auto shrink-0">{(f.size / 1024).toFixed(0)} KB</span>
                            <button onClick={() => handleRemoveUpload(f.id)} className="text-red-400 hover:text-red-600 ml-1"><X className="h-3.5 w-3.5" /></button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between pt-1">
                    <label className="flex items-center gap-2 cursor-pointer" data-testid="send-copy-checkbox">
                      <Checkbox
                        checked={emailForm.send_copy}
                        onCheckedChange={v => setEmailForm(prev => ({ ...prev, send_copy: v }))}
                      />
                      <span className="text-sm text-gray-600">Send a copy to myself</span>
                    </label>
                    <Button
                      data-testid="send-email-btn"
                      onClick={handleSendEmail}
                      disabled={sending}
                      className="bg-[#0073ea] hover:bg-[#0060c2] text-white px-6"
                    >
                      <Send className="h-4 w-4 mr-2" />
                      {sending ? 'Sending...' : 'Send Email'}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {emails.length > 0 && (
              <>
                <Separator className="bg-gray-200 my-4" />
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-3">Email History</p>
                <div className="space-y-2">
                  {emails.map(em => (
                    <div key={em.id} data-testid={`email-${em.id}`} className="p-3 rounded-lg bg-[#f6f7fb] border border-gray-200">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-gray-900">{em.subject}</p>
                        <Badge variant="outline" className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs">{em.status}</Badge>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">To: {em.to_email} - {new Date(em.sent_at).toLocaleString()}</p>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* ────── BEST BUYERS ────── */}
      <BestBuyersCard lead={lead} onUseInBlast={(ids) => { setBlastPreselect(ids); setTimeout(() => document.getElementById('blast-buyers-section')?.scrollIntoView({ behavior: 'smooth' }), 100); }} />

      {/* ────── DEAL BLAST: SEND TO BUYERS ────── */}
      <div id="blast-buyers-section">
        <DealBlastSection
          preselectedIds={blastPreselect}
          lead={lead}
          recipients={buyers.map(b => ({ id: b.id, name: b.name, email: b.email, company: b.company, category: b.category }))}
          recipientType="buyers"
          onSendComplete={handleBlastSendComplete}
        />
      </div>

      {/* ────── DEAL BLAST: SEND TO WHOLESALERS ────── */}
      <div id="blast-wholesalers-section">
        <DealBlastSection
          lead={lead}
          recipients={wholesalers.map(w => ({ id: w.id, name: w.name, email: w.email, company: w.company }))}
          recipientType="wholesalers"
          onSendComplete={handleBlastSendComplete}
        />
      </div>

      {/* ────── BLAST HISTORY ────── */}
      <BlastHistorySection leadId={id} refreshKey={blastRefreshKey} />

      {/* ────── DRIP CAMPAIGNS ────── */}
      <DripAssignmentCard targetType="lead" targetId={id} />

      <TasksPanel leadId={lead.id} />

      {/* ────── ACTIVITY TIMELINE ────── */}
      <div id="activity-section">
        <ActivityTimeline activities={activities} onAddNote={handleAddActivity} />
      </div>

      {/* Notes */}
      <Card className="bg-white border-gray-200 shadow-sm">
        <CardContent className="p-6">
          <Field label="Internal Notes">
            <Textarea data-testid="detail-notes" value={lead.notes || ''} onChange={e => update('notes', e.target.value)} rows={3} className={`${inputClass} resize-none`} placeholder="Internal notes..." />
          </Field>
        </CardContent>
      </Card>

      {/* Contract Preview Modal — Interactive with Annotations */}
      <Dialog open={showPreview} onOpenChange={(open) => { setShowPreview(open); if (!open) setAnnotations([]); }}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="preview-modal">
          <DialogHeader>
            <DialogTitle className="text-xl text-gray-900">Contract Preview</DialogTitle>
            <p className="text-xs text-gray-500 mt-1">Click to add text. Drag to move. Hover for size controls. Double-click to edit.</p>
          </DialogHeader>
          {previewPdf && (
            <div className="flex flex-col items-center gap-4 mt-2">
              {previewPages > 1 && (
                <div className="flex items-center gap-3">
                  <Button variant="ghost" size="sm" disabled={previewPage <= 1} onClick={() => setPreviewPage(p => p - 1)}>Prev</Button>
                  <span className="text-sm text-gray-500">Page {previewPage} of {previewPages}</span>
                  <Button variant="ghost" size="sm" disabled={previewPage >= previewPages} onClick={() => setPreviewPage(p => p + 1)}>Next</Button>
                </div>
              )}
              <div
                ref={previewContainerRef}
                className="light-zone border border-gray-200 rounded-lg overflow-hidden shadow-sm relative cursor-crosshair select-none"
                data-testid="pdf-annotation-area"
                onClick={(e) => {
                  if (draggingId) return;
                  const rect = e.currentTarget.getBoundingClientRect();
                  const x = e.clientX - rect.left;
                  const y = e.clientY - rect.top;
                  const xPct = (x / rect.width) * 100;
                  const yPct = (y / rect.height) * 100;
                  if (e.target.tagName === 'INPUT' || e.target.closest('[data-annotation]')) return;
                  setAnnotations(prev => [...prev, {
                    id: Date.now(),
                    page_num: previewPage - 1,
                    x_percent: xPct,
                    y_percent: yPct,
                    text: '',
                    font_size: 11,
                    editing: true,
                  }]);
                }}
                onMouseMove={(e) => {
                  if (!draggingId) return;
                  const rect = e.currentTarget.getBoundingClientRect();
                  const xPct = ((e.clientX - rect.left - dragOffsetRef.current.x) / rect.width) * 100;
                  const yPct = ((e.clientY - rect.top - dragOffsetRef.current.y) / rect.height) * 100;
                  setAnnotations(prev => prev.map(a => a.id === draggingId ? { ...a, x_percent: Math.max(0, Math.min(95, xPct)), y_percent: Math.max(0, Math.min(95, yPct)) } : a));
                }}
                onMouseUp={() => { if (draggingId) setDraggingId(null); }}
                onMouseLeave={() => { if (draggingId) setDraggingId(null); }}
              >
                <Document
                  file={`data:application/pdf;base64,${previewPdf}`}
                  onLoadSuccess={({ numPages: n }) => setPreviewPages(n)}
                  loading={<div className="h-[600px] w-[500px] flex items-center justify-center"><div className="h-6 w-6 border-2 border-[#0073ea] border-t-transparent rounded-full animate-spin" /></div>}
                >
                  <Page pageNumber={previewPage} renderTextLayer={false} renderAnnotationLayer={false} width={600} />
                </Document>
                {/* Render annotations for current page */}
                {pageAnnotations.map(ann => (
                  <div
                    key={ann.id}
                    data-annotation="true"
                    data-testid={`annotation-${ann.id}`}
                    className="absolute"
                    style={{ left: `${ann.x_percent}%`, top: `${ann.y_percent}%`, transform: 'translate(-2px, -4px)', zIndex: draggingId === ann.id ? 50 : 10 }}
                  >
                    {ann.editing ? (
                      <input
                        autoFocus
                        className="bg-yellow-50/90 border border-yellow-400 rounded px-1 py-0.5 text-xs text-gray-900 outline-none min-w-[100px] shadow-sm"
                        style={{ fontSize: `${ann.font_size}px` }}
                        defaultValue={ann.text}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const val = e.target.value.trim();
                            if (val) {
                              setAnnotations(prev => prev.map(a => a.id === ann.id ? { ...a, text: val, editing: false } : a));
                            } else {
                              setAnnotations(prev => prev.filter(a => a.id !== ann.id));
                            }
                          }
                          if (e.key === 'Escape') {
                            setAnnotations(prev => prev.filter(a => a.id !== ann.id));
                          }
                        }}
                        onBlur={(e) => {
                          const val = e.target.value.trim();
                          if (val) {
                            setAnnotations(prev => prev.map(a => a.id === ann.id ? { ...a, text: val, editing: false } : a));
                          } else {
                            setAnnotations(prev => prev.filter(a => a.id !== ann.id));
                          }
                        }}
                      />
                    ) : (
                      <div className="group flex flex-col items-start" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-0.5">
                          {/* Drag handle + text */}
                          <span
                            className={`bg-yellow-100/80 border border-yellow-300 rounded px-1 py-0.5 text-gray-900 select-none ${draggingId === ann.id ? 'cursor-grabbing ring-2 ring-blue-400' : 'cursor-grab'}`}
                            style={{ fontSize: `${ann.font_size}px` }}
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              const container = previewContainerRef.current;
                              if (!container) return;
                              const containerRect = container.getBoundingClientRect();
                              const annX = (ann.x_percent / 100) * containerRect.width;
                              const annY = (ann.y_percent / 100) * containerRect.height;
                              dragOffsetRef.current = {
                                x: e.clientX - containerRect.left - annX,
                                y: e.clientY - containerRect.top - annY,
                              };
                              setDraggingId(ann.id);
                            }}
                            onDoubleClick={() => setAnnotations(prev => prev.map(a => a.id === ann.id ? { ...a, editing: true } : a))}
                            title="Drag to move · Double-click to edit"
                          >
                            {ann.text}
                          </span>
                          {/* Delete button */}
                          <button
                            onClick={() => setAnnotations(prev => prev.filter(a => a.id !== ann.id))}
                            className="opacity-0 group-hover:opacity-100 h-4 w-4 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center transition-opacity"
                          >
                            ×
                          </button>
                        </div>
                        {/* Size controls — visible on hover */}
                        <div className="opacity-0 group-hover:opacity-100 flex items-center gap-0.5 mt-0.5 transition-opacity">
                          <button
                            data-testid={`size-down-${ann.id}`}
                            onClick={() => setAnnotations(prev => prev.map(a => a.id === ann.id ? { ...a, font_size: Math.max(6, a.font_size - 1) } : a))}
                            className="h-4 w-4 rounded bg-gray-200 text-gray-600 text-[10px] flex items-center justify-center hover:bg-gray-300 font-bold"
                          >
                            A↓
                          </button>
                          <span className="text-[9px] text-gray-400 min-w-[18px] text-center">{ann.font_size}</span>
                          <button
                            data-testid={`size-up-${ann.id}`}
                            onClick={() => setAnnotations(prev => prev.map(a => a.id === ann.id ? { ...a, font_size: Math.min(36, a.font_size + 1) } : a))}
                            className="h-4 w-4 rounded bg-gray-200 text-gray-600 text-[10px] flex items-center justify-center hover:bg-gray-300 font-bold"
                          >
                            A↑
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {annotations.length > 0 && (
                <p className="text-xs text-amber-600 font-medium">{annotations.filter(a => !a.editing && a.text).length} annotation{annotations.filter(a => !a.editing && a.text).length !== 1 ? 's' : ''} added — these will be included when you generate</p>
              )}
              <div className="flex gap-2 w-full">
                <Button
                  data-testid="generate-from-preview-btn"
                  onClick={handleGenerate}
                  disabled={generating}
                  className="flex-1 bg-[#0073ea] hover:bg-[#0060c2] text-white"
                >
                  {generating ? <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : 'Generate Contract'}
                </Button>
                {annotations.length > 0 && (
                  <Button variant="ghost" onClick={() => setAnnotations([])} className="text-gray-500 text-xs">Clear All</Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Sold Price Dialog */}
      <Dialog open={showSoldPriceDialog} onOpenChange={setShowSoldPriceDialog}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-sm" data-testid="sold-price-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl text-gray-900">Close Deal</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-gray-500">Enter the final sold price to close this deal and calculate your assignment fee.</p>
            <div className="space-y-2">
              <Label className="text-gray-700">Sold Price ($)</Label>
              <Input
                data-testid="sold-price-input"
                type="number"
                value={pendingSoldPrice}
                onChange={e => setPendingSoldPrice(e.target.value)}
                placeholder="Enter sold price"
                className="bg-white border-gray-300 text-gray-900 text-lg font-mono"
                autoFocus
              />
            </div>
            {pendingSoldPrice && lead.under_contract_price > 0 && (
              <div className={`p-3 rounded-lg border ${(parseFloat(pendingSoldPrice) - lead.under_contract_price) > 0 ? 'bg-emerald-50 border-emerald-100' : 'bg-red-50 border-red-100'}`}>
                <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-0.5">Assignment Fee</p>
                <p className={`text-lg font-bold font-mono ${(parseFloat(pendingSoldPrice) - lead.under_contract_price) > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  ${(parseFloat(pendingSoldPrice) - lead.under_contract_price).toLocaleString()}
                </p>
              </div>
            )}
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setShowSoldPriceDialog(false)} className="flex-1 border-gray-300 text-gray-700">
                Cancel
              </Button>
              <Button
                data-testid="confirm-sold-price-btn"
                onClick={handleConfirmSoldPrice}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                Close Deal
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
